import { Component, Element, ComponentInterface, h, VNode, Host, Prop } from '@stencil/core';

import { UniTemplate } from '@uiwebkit/common';
import { uniWatermark } from '@uni/license';

import { Tween, Circ } from '../utils/gsap';

@Component({
  tag: 'uni-net',
  // styleUrl: '../styles/net.css',
})
export class UniNetComponent implements ComponentInterface {

  @Element() el: HTMLElement;

  @Prop({ reflect: true }) color: string = '156,217,249';
  @Prop({ reflect: true }) size: number = 3;
  @Prop({ reflect: true }) ppi: number = 50;
  @Prop({ reflect: true }) timeout: number = 50;
  @Prop({ reflect: true }) x: 'left' | 'center' | 'right' = 'center';
  @Prop({ reflect: true }) y: 'top' | 'center' | 'bottom' = 'center';
  @Prop({ reflect: true }) xShift: number = 1;
  @Prop({ reflect: true }) yShift: number = 1;

  private active: boolean = true;
  private count: number = 0;
  private target: any = {};
  // private canvas: any = {};
  private ctx: any = {};
  private points: any[] = [];
  private height: number = 0;

  render(): VNode {
    return UniTemplate(
      <Host>
        <canvas style={{ position: 'fixed' }}></canvas>
        <slot />
      </Host>,
    );
  }

  componentDidLoad(): void {
    uniWatermark('pro');

    this.init();
  }

  private init(): void {
    this.height = this.getHeight();
    this.active = window.scrollY <= this.el.offsetHeight;
    this.target = this.getTarget(this.x, this.y);

    // Call Core Functions
    this.initCanvas();
    this.initAnimation();
    this.addListeners();
  }

  private initCanvas(): void {
    const ppi: number = this.ppi >= 10 ? this.ppi : 10;
    let canvas: HTMLCanvasElement = this.el.children[0] as HTMLCanvasElement;
    canvas.width = document.body.getBoundingClientRect().width;
    canvas.height = this.getHeight();
    this.ctx = canvas.getContext('2d');

    // create points
    this.points = [];

    for (let x = 0; x < canvas.width / ppi; x++) {
      for (let y = 0; y < canvas.height / ppi; y++) {
        let px: number = Math.floor((Math.random() * 2 + (x - 1)) * ppi);
        let py: number = Math.floor((Math.random() * 2 + (y - 1)) * ppi);
        let p = { x: px, originX: px, y: py, originY: py };

        this.points.push(p);
      }
    }

    // for each point find the 5 closest points
    for (let i = 0; i < this.points.length; i++) {
      let closest = [];
      let p1 = this.points[i];

      for (let j = 0; j < this.points.length; j++) {
        let p2 = this.points[j];

        if (!(p1 == p2)) {
          let placed: boolean = false;

          for (let k = 0; k < 5; k++) {
            if (!placed) {
              if (closest[k] == undefined) {
                closest[k] = p2;
                placed = true;
              }
            }
          }

          for (let k = 0; k < 5; k++) {
            if (!placed) {
              if (this.getDistance(p1, p2) < this.getDistance(p1, closest[k])) {
                closest[k] = p2;
                placed = true;
              }
            }
          }
        }
      }
      p1.closest = closest;
    }

    // assign a circle to each point
    for (let i in this.points) {
      this.points[i].circle = this.getCircle(this.points[i]);
    }
  }

  // Event handling
  private addListeners(): void {
    if (!('ontouchstart' in window)) {
      window.addEventListener('mousemove', this.mouseMove.bind(this));
    }

    window.addEventListener('scroll', this.scrollCheck.bind(this));
    // window.addEventListener('resize', this.resize.bind(this));
  }

  private mouseMove(e: MouseEvent): void {
    let x, y: number = 0;
    this.count++;

    if (e.pageX || e.pageY) {
      x = e.pageX;
      y = e.pageY - window.scrollY;
    } else if (e.clientX || e.clientY) {
      x = e.clientX + document.body.scrollLeft + document.documentElement.scrollLeft;
      y = e.clientY + document.body.scrollTop + document.documentElement.scrollTop;
    }

    this.target.x = x;
    this.target.y = y;

    if (this.count > 3) {
      this.animate();
      this.count = 0;
    }
  }

  private scrollCheck(): void {
    const height: number = this.getHeight();

    if (this.height !== height) {
      this.height = this.getHeight();
      this.active = window.scrollY <= this.el.offsetHeight - 300;
      this.target = this.getTarget(this.x, this.y);

      this.initCanvas();

      if (this.active) {
        for (let i in this.points) {
          this.shiftPoint(this.points[i]);
        }
      }
    }
  }

  // private resize(): void {
  //   this.canvas.width = document.body.getBoundingClientRect().width;
  //   this.canvas.height = window.innerHeight;
  // }

  // animation
  private initAnimation(): void {
    this.animate();

    for (let i in this.points) {
      this.shiftPoint(this.points[i]);
    }

    setInterval(() => this.animate(), this.timeout);
  }

  private animate(): void {
    if (this.active) {
      this.ctx.clearRect(0, 0, this.el.offsetWidth, this.el.offsetHeight);

      for (let i in this.points) {
        // detect points in range
        if (this.getDistance(this.target, this.points[i]) < (1000 * this.size)) {
          this.points[i].active = 0.3;
          this.points[i].circle.active = 0.6;
        } else if (this.getDistance(this.target, this.points[i]) < (5000 * this.size)) {
          this.points[i].active = 0.1;
          this.points[i].circle.active = 0.3;
        } else if (this.getDistance(this.target, this.points[i]) < (10000 * this.size)) {
          this.points[i].active = 0.02;
          this.points[i].circle.active = 0.1;
        } else {
          this.points[i].active = 0;
          this.points[i].circle.active = 0;
        }

        this.drawLines(this.points[i]);
        this.points[i].circle.draw(this.points[i].circle.active);
      }
    }
  }

  private shiftPoint(point: any): void {
    if (this.points.some((p) => p.originX === point.originX && p.originY === point.originY)) {
      Tween.to(point, 1 + Math.random(), {
        x: point.originX - 50 + Math.random() * 100,
        y: point.originY - 50 + Math.random() * 100,
        ease: Circ.easeInOut,
        onComplete: () => this.shiftPoint(point),
      });
    }
  }

  // Canvas manipulation
  private drawLines(point: any = {}): void {
    if (point.active) {
      for (let i in point.closest) {
        this.ctx.beginPath();
        this.ctx.moveTo(point.x, point.y);
        this.ctx.lineTo(point.closest[i].x, point.closest[i].y);
        this.ctx.strokeStyle = `rgba(${this.color},${point.active})`;
        this.ctx.stroke();
      }
    }
  }

  private getCircle(pos: any = {}): any {
    const radius: number = Math.round(2 + Math.random() * 2);

    return {
      draw: (active: number): void => {
        if (active) {
          this.ctx.beginPath();
          this.ctx.arc(pos.x, pos.y, radius, 0, 2 * Math.PI, false);
          this.ctx.fillStyle = `rgba(${this.color},${active})`;
          this.ctx.fill();
        }
      },
    };
  }

  // Util
  private getDistance(p1: any, p2: any): number {
    return Math.abs(Math.pow(p1.x - p2.x, 2) + Math.pow(p1.y - p2.y, 2));
  }

  private getTarget(xPos: 'left' | 'center' | 'right', yPos: 'top' | 'center' | 'bottom'): any {
    const height: number = this.getHeight();

    const x: number = (xPos === 'right' ? this.el.offsetWidth : xPos === 'center' ? this.el.offsetWidth / 2 : 0) / (this.xShift || 1);
    const y: number = (yPos === 'bottom' ? height : yPos === 'center' ? height / 2 : 0) / (this.yShift || 1);

    return { x, y };
  }

  private getHeight(): number {
    const height: number = this.el.offsetHeight - window.scrollY;

    return height > 0 ? height < window.innerHeight ? height : window.innerHeight : 0;
  }
}
